/* ──────────────────────────────────────────────────────────────
   src/pages/reports/reservas/SeriesReservasPage.tsx
   ────────────────────────────────────────────────────────────── */
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Stack,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Paper,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useReservasDiarioReport } from "../../../hooks/useReportJob";
import type { ReservasDiaRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/** Fila con campo calculado de tendencia */
type RowWithTrend = ReservasDiaRow & { trend?: number };

/**
 * Suavizado exponencial con tendencia (Holt).
 * - alpha: nivel; beta: tendencia
 * - skipZeros: si true, ignora días con 0 para no hundir la tendencia por fines de semana/festivos.
 * Devuelve las mismas filas con el campo `trend`.
 */
function holtTrend(
  rows: ReservasDiaRow[],
  {
    alpha = 0.3,
    beta = 0.1,
    skipZeros = true,
  }: { alpha?: number; beta?: number; skipZeros?: boolean } = {}
): RowWithTrend[] {
  if (!rows?.length) return [];

  // Aseguramos orden por fecha ascendente
  const ordered = [...rows].sort((a, b) => a.fecha.localeCompare(b.fecha));

  // Inicializaciones
  // Tomamos el primer valor como nivel inicial y la diferencia con el segundo como tendencia inicial
  let yPrev = ordered[0].n;
  let level = yPrev;
  let trend = ordered.length > 1 ? ordered[1].n - ordered[0].n : 0;

  const out: RowWithTrend[] = [];

  for (let i = 0; i < ordered.length; i++) {
    const r = ordered[i];
    // Valor observado (posible ignorar ceros)
    const y = skipZeros && r.n === 0 ? yPrev : r.n;

    // Holt (doble suavizado exponencial)
    const newLevel = alpha * y + (1 - alpha) * (level + trend);
    const newTrend = beta * (newLevel - level) + (1 - beta) * trend;

    // Ajuste para el instante t (aprox. nivel + tendencia)
    const fitted = newLevel + newTrend;

    out.push({
      ...r,
      trend: Number((fitted < 0 ? 0 : fitted).toFixed(2)),
    });

    // Avanzamos
    yPrev = y;
    level = newLevel;
    trend = newTrend;
  }

  // Devolvemos en el mismo orden que vienen del backend
  // (ya están ordenados ascendente; si quisieras mantener el orden original, reordena aquí)
  return out;
}

export default function SeriesReservasPage() {
  const { t } = useTranslation<"translation">();
  /* rango de fechas */
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));

  /* worker */
  const { run, useStatus } = useReservasDiarioReport();
  const [jobId, setJobId] = useState<string>();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    const { jobId } = await run({ desde, hasta });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  // Datos para el gráfico con la serie original + la tendencia suavizada
  const chartData: RowWithTrend[] = useMemo(() => {
    if (!status?.done || !status.result?.rows) return [];
    // Ajusta alpha/beta si quieres más/menos suavizado
    return holtTrend(status.result.rows as ReservasDiaRow[], {
      alpha: 0.3,
      beta: 0.1,
      skipZeros: true,
    });
  }, [status]);

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("seriesReservas.titulo")}</Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2}>
        <TextField
          type="date"
          label={t("seriesReservas.filtros.desde")}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t("seriesReservas.filtros.hasta")}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />
        <Button variant="contained" onClick={lanzar}>
          {t("seriesReservas.filtros.generar")}
        </Button>
      </Stack>

      {/* progreso */}
      {status && !status.done && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={24} />
          <Typography> {t("seriesReservas.estado.generando")}</Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && status.result && (
        <>
          <Alert severity="success" sx={{ width: "fit-content" }}>
            XLSX:&nbsp;
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {t("seriesReservas.estado.descargar")}
            </Button>
          </Alert>

          <Paper sx={{ p: 2, height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                {/* ────────────────────────────────────────
                    tickFormatter formatea cada tick del eje X
                    labelFormatter formatea la etiqueta del tooltip
                    ──────────────────────────────────────── */}
                <XAxis dataKey="fecha" tickFormatter={(d) => dayjs(d).format("DD-MM")} />
                <YAxis allowDecimals={false} />
                <Tooltip labelFormatter={(d) => dayjs(d).format("DD-MM-YYYY")} />
                <Legend />

                {/* Serie original */}
                <Line
                  type="monotone"
                  dataKey="n"
                  name={t("seriesReservas.leyenda.original") ?? "Clases/día"}
                  stroke="#1976d2"
                  dot={false}
                />

                {/* Tendencia suavizada/extrapolada */}
                <Line
                  type="monotone"
                  dataKey="trend"
                  name={t("seriesReservas.leyenda.tendencia") ?? "Tendencia (suavizada)"}
                  stroke="#d32f2f"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Stack>
  );
}
